import { ZoneId } from '@js-joda/core';
import { Link } from '@mui/material';
import React from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  Edit,
  Filter,
  FunctionField,
  ReferenceArrayField,
  ReferenceField,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
} from 'react-admin';
import { Studio } from '../../graphql/types';
import { IDSearch } from '../reusable/IDSearch';
import AutocompleteClientInput from './components/AutocompleteClientInput';
import ClipboardField from './components/ClipboardField';
import InfiniteList from './components/InfiniteList';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyDeleteButton } from './components/WriteOnlyDeleteButton';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';

const canGrantEligibilityHelperText =
  'Should usually be true. If false, this studio will not grant app access via the client "Uses Fitness Center For Eligibility" flag. Only disable for specific client use cases.';

const timezoneChoices = ZoneId.getAvailableZoneIds()
  .sort()
  .map((zone) => ({
    id: zone,
    name: zone,
  }));

const StudioFilter: React.FC = (props) => (
  <Filter {...props}>
    <IDSearch label="Name" source="name" alwaysOn />
  </Filter>
);

export const StudioList: React.FC = () => (
  <InfiniteList filters={<StudioFilter />}>
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <ClipboardField source="id" sortable={false} />
      <TextField source="name" sortable={false} />
      <TextField source="client.name" label="Client Name" sortable={false} />
      <BooleanField source="isVirtual" label="Virtual" sortable={false} />
      <BooleanField source="isArchived" label="Archived" sortable={false} />
      <WriteOnlyEditButton />
      <ShowButton />
      <WriteOnlyDeleteButton mutationMode="undoable" />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

export const StudioShow: React.FC = () => (
  <Show>
    <SimpleShowLayout>
      <ClipboardField source="id" label="ID" fontSize={14} />
      <TextField source="name" label="Studio Name" />
      <ClipboardField
        source="mboStudioId"
        label="MBO Studio ID"
        fontSize={14}
      />
      <FunctionField
        label="Studio Login Page"
        render={(record: Studio) => {
          const url = `https://clients.mindbodyonline.com/ASP/su1.asp?studioid=${record.mboStudioId}`;
          return (
            <Link href={url} target="_blank" rel="noopener noreferrer">
              {record.mboStudioId}
            </Link>
          );
        }}
      />
      <TextField source="mboServiceId" label="MBO Service ID" />
      <TextField source="mboProgramId" label="MBO Program ID" />
      <TextField source="timezone" label="Timezone" />
      <BooleanField source="isCentral" label="Is Central" />
      <BooleanField source="isVirtual" label="Is Virtual" />
      <BooleanField
        source="canGrantEligibility"
        label="Can Grant Eligibility"
      />
      <BooleanField source="isArchived" label="Is Archived" />
      <ReferenceField source="client.id" reference="Client" label="Client">
        <TextField source="name" />
      </ReferenceField>

      <ReferenceArrayField
        source="locations.ids"
        reference="CoachLocation"
        label="Locations"
      >
        <WriteOnlyDatagrid>
          <TextField source="name" />
          <ShowButton />
        </WriteOnlyDatagrid>
      </ReferenceArrayField>

      <ReferenceArrayField
        source="mboContractMappings.ids"
        reference="MboContractMapping"
        label="MBO Contract Mappings"
      >
        <WriteOnlyDatagrid bulkActionButtons={false}>
          <TextField source="mboServiceId" label="MBO Service ID" />
          <TextField source="employeeType" label="Employee Type" />
          <ShowButton />
        </WriteOnlyDatagrid>
      </ReferenceArrayField>
    </SimpleShowLayout>
  </Show>
);

export const StudioEdit: React.FC = () => (
  <Edit mutationMode="pessimistic" redirect={false}>
    <SimpleForm>
      <TextInput source="name" label="Studio Name" />
      <TextInput source="mboStudioId" label="MBO Studio ID" />
      <TextInput source="mboServiceId" label="MBO Service ID" />
      <TextInput
        source="mboProgramId"
        label="MBO Program ID"
        helperText="Use upon request to restrict which classes appear in the Exos App."
      />
      <SelectInput
        source="timezone"
        label="Timezone"
        choices={timezoneChoices}
      />
      <AutocompleteClientInput source="client.id" label="Client" />
      <BooleanInput source="isVirtual" label="Virtual" />
      <BooleanInput source="isCentral" label="Central" />
      <BooleanInput
        source="canGrantEligibility"
        label="Can Grant Eligibility"
        helperText={canGrantEligibilityHelperText}
      />
      <BooleanInput
        source="isArchived"
        label="Archived"
        helperText="When this field is true, we will not create MBO profiles for members in this studio"
      />
    </SimpleForm>
  </Edit>
);

export const StudioCreate: React.FC = () => (
  <Create>
    <SimpleForm>
      <TextInput source="name" label="Studio Name" />
      <TextInput source="mboStudioId" label="MBO Studio ID" />
      <TextInput source="mboServiceId" label="MBO Service ID" />
      <SelectInput
        source="timezone"
        label="Timezone"
        choices={timezoneChoices}
      />
      <AutocompleteClientInput source="client.id" label="Client" />
      <BooleanInput source="isVirtual" label="Virtual" />
      <BooleanInput source="isCentral" label="Central" />
      <BooleanInput
        source="canGrantEligibility"
        label="Can Grant Eligibility"
        defaultValue
        helperText={canGrantEligibilityHelperText}
      />
    </SimpleForm>
  </Create>
);
